import { GetterTree } from 'vuex';
import { VasState } from './types';
import { RootState } from '../../types';
import { STORE_OPTION_TYPES } from '@/common/constants'
import { placeAddressIsValid } from '@/common/utils'

export function hasPickupAvailable (merchant: MerchantStore) {
  if (merchant) {
    return !!merchant.configuration.pickupNow
  }
  return false
}

export function hasDelivery (merchant: MerchantStore) {
  if (merchant) {
    return !!merchant.configuration?.deliveryType
  }
  return false
}

export function hasTimeSlotDelivery (merchant: MerchantStore) {
  if (merchant) {
    return merchant.configuration?.deliveryType === 'DEFAULT'
  }
  return false
}

export function hasEcommerceDelivery (merchant: MerchantStore) {
  if (merchant) {
    return merchant.configuration?.deliveryType === 'ECOMMERCE'
  }
  return false
}

export const getters: GetterTree<VasState, RootState> = {
  merchant (state) {
    return state.shops.find(({ id }) => String(id) === String(state.vasId))
  },

  vasses (state): Array<MerchantStore> {
    return state.shops.filter(({ shopId }) => String(shopId) === String(state.shopId))
  },

  hasPickupAvailable (state: VasState, getters) {
    return hasPickupAvailable(getters.merchant)
  },

  hasScheduledPickupAvailable (state: VasState, getters) {
    if (getters.merchant) {
      return !!getters.merchant.configuration.schedulePickup
    }
    return false
  },

  hasDelivery (state: VasState, getters) {
    return hasDelivery(getters.merchant)
  },

  hasTimeSlotDelivery (state: VasState, getters) {
    return hasTimeSlotDelivery(getters.merchant)
  },

  hasEcommerceDelivery (state: VasState, getters) {
    return hasEcommerceDelivery(getters.merchant)
  },

  isEnabled (state: VasState, getters) {
    if (getters.merchant) {
      return getters.merchant.configuration.enabled
    }
    return false
  },

  isShowPoliticalMessage (state: VasState, getters) {
    if (getters.merchant && getters.merchant.configuration) {
      return getters.merchant.configuration.showPoliticalMessage === true
    }
    return true
  },

  requiresDeliverySlots (state: VasState, getters) {
    if (getters.merchant) {
      return getters.hasTimeSlotDelivery
    }
    return false
  },

  hasNoDeliverySlots (state: VasState, getters) {
    if (getters.hasDelivery) {
      return !getters.hasAvailableSlots
    }

    if (getters.hasScheduledPickupAvailable) {
      return !getters.hasAvailableSlots
    }

    return false
  },

  hasAvailableSlots (state: VasState, getters) {
    if (getters.merchant) {
      return !!getters.merchant.hasAvailableSlots
    }
    return false
  },

  getDeliverySlots (state: VasState, getters) {
    if (getters.hasDelivery) {
      return getters.hasAvailableSlots
    }

    return getters.hasScheduledPickupAvailable && getters.hasAvailableSlots
  },

  shippingDeliveryCost (state: VasState, getters) {
    if (getters.merchant) {
      return getters.merchant.configuration.deliveryCost ? getters.merchant.configuration.deliveryCost : 0
    }
    return false;
  },

  shippingDeliveryThreshold (state: VasState, getters) {
    if (getters.merchant) {
      return getters.merchant.configuration.deliveryThreshold ? getters.merchant.configuration.deliveryThreshold : 0
    }
    return false;
  },

  availableSaleTypes (state: VasState, getters) {
    return getters.vasses.reduce((types: Array<any>, vas: MerchantStore) => {
      if (hasPickupAvailable(vas)) {
        types.push({
          label: 'takeaway',
          value: STORE_OPTION_TYPES.TAKEAWAY,
          vasId: vas.id
        })
      }

      if (hasDelivery(vas)) {
        types.push({
          label: 'delivery',
          value: STORE_OPTION_TYPES.DELIVERY,
          vasId: vas.id
        })
      }
      return types
    }, [])
  },

  shippingAddress (state: VasState): CustomerAddress | null {
    if (!state.shippingAddressPlace || !state.customerDetails) return null
    if (placeAddressIsValid(state.shippingAddressPlace)) {
      const customer = state.customerDetails
      let {
        address
      } = state.shippingAddressPlace
      if (!address && state.shippingAddressPlace?.locality) 
      {
        address = {
          city: state.shippingAddressPlace.locality,
          countryName: state.shippingAddressPlace.country,
          state: state.shippingAddressPlace.administrative_area_level_2,
          houseNumber: state.shippingAddressPlace.street_number,
          street: `${state.shippingAddressPlace.route}`,
          postalCode: state.shippingAddressPlace.postal_code,
        }
      }

      return {
        city: address.city,
        country: address.countryName,
        description: customer.notes,
        state: address.state,
        streetNumber: address.houseNumber,
        // streetAddress: `${route}, ${street_number}, ${locality}, ${postal_code}`,
        streetAddress: address.street,
        postalCode: address.postalCode,
        givenName: customer.firstname,
        familyName: customer.lastname,
        telephone: customer.telephone
      }
    }

    return null;
  },

  customerDetails (state: VasState): BasicCustomerDetails | null {
    if (!state.customerDetails) return null
    const customer = state.customerDetails
    return {
      givenName: customer.firstname,
      familyName: customer.lastname,
      telephone: customer.telephone,
      email: customer.email,
      fiscalCode: customer.fiscalCode,
      pickupPoint: customer.pickupPoint,
      message: customer.message,
    }
  },

  hasValidAddressAndShipping (state: VasState, getters) {
    if (getters.hasDelivery && !getters.shippingAddress) return false
    return state.customerDetails
  }
};
